// Calls explorer: filterable, cursor-paginated call log with a right-side
// detail drawer (deep-linkable via ?call=<id>, guide-filterable via ?guideId=).

import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { listGuides } from "../services/guides";
import {
  listCalls,
  type CallRecord,
  type SentimentLabel,
} from "../services/analytics";
import { CallDrawer } from "../components/analytics/CallDrawer";
import {
  AnalysisBadge,
  ModeChip,
  InputModeToggle,
  SentimentCell,
  SourceChip,
  TrafficToggle,
} from "../components/analytics/bits";
import {
  apiInputMode,
  apiSource,
  excerpt,
  formatDateTime,
  formatDurationMs,
  parseSourceParam,
  truncateUid,
  type InputModeFilter,
} from "../components/analytics/format";

const PAGE_SIZE = 50;
const SENTIMENTS: SentimentLabel[] = ["positive", "neutral", "negative", "mixed"];

function defaultFromDate(): string {
  const d = new Date(Date.now() - 30 * 86_400_000);
  return d.toISOString().slice(0, 10);
}

function defaultToDate(): string {
  return new Date().toISOString().slice(0, 10);
}

export function CallsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const guideId = searchParams.get("guideId") || "";
  const selectedCallId = searchParams.get("call");
  // Traffic source is URL-persisted (?source=studio_bench|all) so filtered
  // views are shareable; "app" is the default and omitted from the URL.
  const traffic = parseSourceParam(searchParams.get("source"));

  const [guideOptions, setGuideOptions] = useState<{ id: string; name: string }[]>([]);
  const [mode, setMode] = useState<InputModeFilter>("all");
  const [sentiment, setSentiment] = useState<"" | SentimentLabel>("");
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [fromDate, setFromDate] = useState(defaultFromDate);
  const [toDate, setToDate] = useState(defaultToDate);

  const [calls, setCalls] = useState<CallRecord[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Companion filter options (reuses the guides service; failure is non-fatal —
  // the dropdown just stays empty).
  useEffect(() => {
    listGuides()
      .then((gs) => setGuideOptions(gs.map((g) => ({ id: g.id, name: g.name || g.id }))))
      .catch(() => setGuideOptions([]));
  }, []);

  const query = useMemo(() => {
    const from = fromDate ? new Date(`${fromDate}T00:00:00`).toISOString() : undefined;
    const to = toDate ? new Date(`${toDate}T23:59:59.999`).toISOString() : undefined;
    return {
      guideId: guideId || undefined,
      input_mode: apiInputMode(mode),
      source: apiSource(traffic),
      sentiment: sentiment || undefined,
      flagged: flaggedOnly || undefined,
      from,
      to,
      limit: PAGE_SIZE,
    };
  }, [guideId, mode, traffic, sentiment, flaggedOnly, fromDate, toDate]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    listCalls(query)
      .then((r) => {
        if (cancelled) return;
        setCalls(r.items);
        setNextCursor(r.nextCursor ?? null);
      })
      .catch((e) => {
        if (cancelled) return;
        setCalls([]);
        setNextCursor(null);
        setError(e instanceof Error ? e.message : "Failed to load calls");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [query]);

  async function loadMore() {
    if (!nextCursor) return;
    setLoadingMore(true);
    try {
      const r = await listCalls({ ...query, cursor: nextCursor });
      setCalls((prev) => [...prev, ...r.items]);
      setNextCursor(r.nextCursor ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load more calls");
    } finally {
      setLoadingMore(false);
    }
  }

  const setParam = useCallback(
    (key: string, value: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value) next.set(key, value);
          else next.delete(key);
          return next;
        },
        { replace: key === "call" },
      );
    },
    [setSearchParams],
  );

  return (
    <div className="list-page calls-page">
      <div className="list-toolbar">
        <h1>Calls</h1>
        <div className="spacer" />
        <TrafficToggle
          value={traffic}
          onChange={(s) => setParam("source", s === "app" ? null : s)}
        />
        <InputModeToggle value={mode} onChange={setMode} />
      </div>

      <div className="filters">
        <div className="filter">
          <label>Companion</label>
          <select value={guideId} onChange={(e) => setParam("guideId", e.target.value || null)}>
            <option value="">All companions</option>
            {guideOptions.map((g) => (
              <option key={g.id} value={g.id}>{g.name}</option>
            ))}
            {guideId && !guideOptions.some((g) => g.id === guideId) && (
              <option value={guideId}>{guideId}</option>
            )}
          </select>
        </div>
        <div className="filter">
          <label>Sentiment</label>
          <select
            value={sentiment}
            onChange={(e) => setSentiment(e.target.value as "" | SentimentLabel)}
          >
            <option value="">Any sentiment</option>
            {SENTIMENTS.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <div className="filter">
          <label>From</label>
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </div>
        <div className="filter">
          <label>To</label>
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </div>
        <label className="check-filter">
          <input
            type="checkbox"
            checked={flaggedOnly}
            onChange={(e) => setFlaggedOnly(e.target.checked)}
          />
          Flagged only
        </label>
      </div>

      {loading ? (
        <div className="state-box">Loading calls…</div>
      ) : error ? (
        <div className="state-box error">
          Couldn't load calls: {error}
        </div>
      ) : calls.length === 0 ? (
        <div className="state-box">No calls match these filters.</div>
      ) : (
        <>
          <div className="result-count">
            {calls.length} call{calls.length === 1 ? "" : "s"}
            {nextCursor ? " loaded" : ""}
          </div>
          <div className="table-scroll">
            <table className="guide-table calls-table">
              <thead>
                <tr>
                  <th className="no-sort">Started</th>
                  <th className="no-sort">User</th>
                  <th className="no-sort">Companion</th>
                  <th className="no-sort">Mode</th>
                  <th className="no-sort">Duration</th>
                  <th className="no-sort">Sentiment</th>
                  <th className="no-sort">Flags</th>
                  <th className="no-sort">Summary</th>
                </tr>
              </thead>
              <tbody>
                {calls.map((c) => (
                  <tr
                    key={c.id}
                    className={`guide-row${selectedCallId === c.id ? " selected" : ""}`}
                    onClick={() => setParam("call", c.id)}
                  >
                    <td className="voice-cell nowrap">{formatDateTime(c.startedAt)}</td>
                    <td className="mono" title={c.uid}>{truncateUid(c.uid)}</td>
                    <td>{c.guideName || c.guideId}</td>
                    <td className="nowrap">
                      <ModeChip call={c} /> <SourceChip source={c.source} />
                    </td>
                    <td className="nowrap">
                      {formatDurationMs(c.durationMs)}
                      {c.hasRecording && <span className="play-affordance" title="Recording"> ▶</span>}
                    </td>
                    <td><SentimentCell score={c.sentimentScore} /></td>
                    <td>
                      {c.flags.length > 0 ? (
                        <span className="chip chip-flag" title={c.flags.join(", ")}>
                          {c.flags.length === 1 ? c.flags[0] : `${c.flags.length} flags`}
                        </span>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td className="summary-cell">
                      {excerpt(c.callSummary)} <AnalysisBadge status={c.analysisStatus} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {nextCursor && (
            <div className="load-more-row">
              <button className="btn" onClick={loadMore} disabled={loadingMore}>
                {loadingMore ? "Loading…" : "Load more"}
              </button>
            </div>
          )}
        </>
      )}

      {selectedCallId && (
        <CallDrawer callId={selectedCallId} onClose={() => setParam("call", null)} />
      )}
    </div>
  );
}
